/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { View } from "react-native";
import ManagerDashboardWidget from "./ManagerDashboardWidget";

import { Accordion, LoadingDots, Text } from "UpduoLibrary";
import {
  upduoPrimaryBlue,
  upduoMediumGray,
  upduoLightGray,
} from "config/UpduoColors";
import { useQuery } from "@apollo/client";
import {
  GetActiveSummitCurriculumQuery,
  GetPastSummitCurriculumQuery,
} from "graphql/internal/generated";
import {
  GET_ACTIVE_SUMMIT_CURRICULUM,
  GET_PAST_SUMMIT_CURRICULUM,
} from "graphql/internal/Queries";
import { getNodes } from "config/Utilities";
import { NodeType } from "config/UtilityTypes";

import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function TopicInformationWidget() {
  const { loading: activeSummitsLoading, data: activeSummitData } =
    useQuery<GetActiveSummitCurriculumQuery>(GET_ACTIVE_SUMMIT_CURRICULUM, {
      fetchPolicy: "network-only",
      onError: (error) => {
        console.error("loadActiveSummits error:", error);
        UpduoAnalytics.logEvent(
          "app-tpoicinpormationwidget-loadactivesummits-error",
          {
            error,
          }
        );
      },
    });

  const { loading: pastSummitsLoading, data: pastSummitData } =
    useQuery<GetPastSummitCurriculumQuery>(GET_PAST_SUMMIT_CURRICULUM, {
      fetchPolicy: "network-only",
      onError: (error) => {
        console.error("loadPastSummits error:", error);
        UpduoAnalytics.logEvent(
          "app-topicinformationwidget-loadpastsummits-error",
          {
            error,
          }
        );
      },
    });

  const activeSummits = getNodes(activeSummitData?.userActiveSummits);
  // Filter past summits to only include summits that overlap with this month
  const now = new Date();
  const firstOfTheMonth = new Date(now.getFullYear(), now.getMonth());
  const pastSummits = getNodes(pastSummitData?.userPastSummits).filter(
    (summit) => new Date(summit.endDate) >= firstOfTheMonth
  );

  return (
    <ManagerDashboardWidget
      title={"Topic Information"}
      style={{
        flex: 1,
        minHeight: 180,
      }}
    >
      {activeSummitsLoading || pastSummitsLoading ? (
        <View
          style={{
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: 10,
          }}
        >
          <LoadingDots />
        </View>
      ) : !activeSummits.length && !pastSummits.length ? (
        <View
          style={{
            width: 220,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Text style={{ color: upduoLightGray }}>
            {"No topic set for this location"}
          </Text>
        </View>
      ) : (
        <>
          {[
            activeSummits?.map((summit) => (
              <SummitDescription
                summit={summit}
                key={summit.id}
                isActive={true}
              />
            )),
            pastSummits?.map((summit) => (
              <SummitDescription
                summit={summit}
                key={summit.id}
                isActive={false}
              />
            )),
          ]}
        </>
      )}
    </ManagerDashboardWidget>
  );
}

function SummitDescription({
  summit,
  isActive,
}: {
  summit: NodeType<GetActiveSummitCurriculumQuery, "userActiveSummits">;
  isActive: boolean;
}) {
  const skills = summit.skillModules
    .map((skillModule) => getNodes(skillModule.publishedSkillList))
    .flat();
  return (
    <View style={{ marginBottom: 10 }}>
      <Text>
        <Text isHeading={true}>
          {isActive ? "Current Event: " : "Previous event in month: "}
        </Text>
        <Text>{summit.name}</Text>
      </Text>
      <Accordion
        head={
          <Text
            isHeading={true}
          >{`Lessons in event's topic(s): (${skills.length})`}</Text>
        }
        body={skills.map((skill) => (
          <View
            key={skill.id}
            style={{ flexDirection: "row", paddingBottom: 3, marginRight: 20 }}
          >
            <Text style={{ color: upduoMediumGray, marginLeft: 17 }}>
              {`${skill.order + 1}.`}
            </Text>
            <Text style={{ color: upduoMediumGray, marginLeft: 10 }}>
              {skill.name}
            </Text>
          </View>
        ))}
        chevronColor={upduoPrimaryBlue}
      />
    </View>
  );
}
